import { useEffect, useState } from 'react';
import useSWR from 'swr';
import { getRoomVisualizations, type RoomVisualization } from '@/services/generation.service';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { RoomVisualizeResult } from './room-visualize-result';
import './room-visualize-history.css';

const active = (record: RoomVisualization) => record.status === 'queued' || record.status === 'processing';
const label = (value: string | null, fallback = 'Unknown') => value?.replace(/-/g, ' ') || fallback;

const stylesLabel = (styles: unknown) => (Array.isArray(styles) ? styles : [])
  .filter((style): style is string => typeof style === 'string' && style.trim().length > 0)
  .map(style => label(style))
  .join(' + ') || 'Products';

export function RoomVisualizeHistory({ sdkUrl }: { sdkUrl: string }) {
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<RoomVisualization | null>(null);
  const { data, error, isLoading, isValidating, mutate } = useSWR(
    ['room-visualize-history', page],
    () => getRoomVisualizations(page, 10),
    { refreshInterval: current => current?.data.some(active) ? 5000 : 0 },
  );
  useEffect(() => {
    if (data && page > Math.max(1, data.pagination.pages)) setPage(1);
  }, [data, page]);
  const detail = data?.data.find(record => record.id === selected?.id) ?? selected;
  const renderBody = () => {
    if (error) return <div role="alert" className="rounded-lg border p-6 space-y-3"><p>Could not load room visualizations.</p><Button onClick={() => void mutate()}>Try again</Button></div>;
    if (isLoading) return <p role="status">Loading room visualizations…</p>;
    if (!data?.data.length) return <div className="rounded-lg border p-12 text-center"><h3>No room visualizations yet</h3><p className="text-muted-foreground">Create a visualization in the playground to see it here.</p></div>;
    return <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">{data.data.map(record => <button
          type="button" key={record.id} onClick={() => setSelected(record)}
          aria-label={`View generation ${record.id}`}
          className="rounded-xl border bg-card p-4 text-left space-y-3 hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
        >
          <div className="flex justify-between items-center gap-2"><Badge variant={record.status === 'failed' ? 'destructive' : 'secondary'} className="capitalize">{record.status}</Badge><time className="text-xs text-muted-foreground" dateTime={record.createdAt}>{new Date(record.createdAt).toLocaleString()}</time></div>
          <div className="grid grid-cols-2 gap-2">
            {record.roomImageUrl && <div className="aspect-square rounded-lg overflow-hidden bg-muted"><img loading="lazy" className="w-full h-full object-cover" src={record.roomImageUrl} alt="Original room" /></div>}
            {/* Created rooms have no source photo: the result takes the whole row. */}
            <div data-testid="result-tile" className={`${record.roomImageUrl ? 'aspect-square' : 'col-span-2 aspect-[2/1]'} rounded-lg overflow-hidden bg-muted flex items-center justify-center`}>{record.resultImageUrl ? <img loading="lazy" className="w-full h-full object-cover" src={record.resultImageUrl} alt="Generated room" /> : <span className="text-sm text-muted-foreground">{active(record) ? label(record.step || record.status) : 'No result image'}</span>}</div>
          </div>
          <p className="capitalize text-sm">{label(record.roomType, 'Unknown room')} · {stylesLabel(record.styles)}</p>
          {record.error && <p className="text-sm text-destructive">{record.error.message}</p>}
        </button>)}</div>;
  };
  return <div className="space-y-5">
    <div className="flex items-center justify-between gap-3">
      <p className="text-sm text-muted-foreground">Room visualizations created with the new visualizer.</p>
      <Button variant="outline" disabled={isValidating} onClick={() => void mutate()}>Refresh</Button>
    </div>
    {renderBody()}
    {data && (page > 1 || data.pagination.pages > 1) && <div className="flex items-center justify-center gap-3">
      <Button variant="outline" disabled={page === 1 || isValidating} onClick={() => setPage(value => value - 1)}>Previous</Button>
      <span className="text-sm">Page {page} of {data.pagination.pages}</span>
      <Button variant="outline" disabled={page >= data.pagination.pages || isValidating} onClick={() => setPage(value => value + 1)}>Next</Button>
    </div>}
    <Dialog open={Boolean(detail)} onOpenChange={open => { if (!open) setSelected(null); }}>
      {detail && <DialogContent className="room-visualize-detail flex flex-col gap-3 p-5 max-w-[min(98vw,1600px)] max-h-[96dvh] overflow-y-auto" onEscapeKeyDown={event => {
        // The SDK uses a native modal dialog for product details. Radix handles
        // Escape at document level, so cancel that inner panel before this one.
        const panel = event.target instanceof Element ? event.target.closest('dialog[open]') : null;
        if (panel instanceof HTMLDialogElement) {
          event.preventDefault();
          // Synthetic cancel events have no native close action. Respect the
          // SDK's preventDefault handler, otherwise perform that action here.
          if (panel.dispatchEvent(new Event('cancel', { cancelable: true })) && panel.open) panel.close();
        }
      }}>
        <DialogHeader><DialogTitle>Room visualization</DialogTitle><DialogDescription className="capitalize">{label(detail.roomType, 'Unknown room')} · {stylesLabel(detail.styles)} · {detail.status}</DialogDescription></DialogHeader>
        {detail.resultImageUrl ? <RoomVisualizeResult record={detail} sdkUrl={sdkUrl} /> : <div className="space-y-4">
          {detail.roomImageUrl && <img className="w-full max-h-[65vh] object-contain rounded-lg" src={detail.roomImageUrl} alt="Original room photo" />}
          {active(detail) && <p role="status">Generation in progress: {label(detail.step || detail.status)}. This view refreshes automatically.</p>}
        </div>}
        {detail.error && <p role="alert" className="text-destructive">{detail.error.message}</p>}
      </DialogContent>}
    </Dialog>
  </div>;
}
